/**
 * scopedBlobStore — the deep seam for a portfolio-scoped list persisted to KV.
 *
 * A scoped list (trades, watchlist, snapshots, journal, thinkPieces) is stored as
 * either `T[]` (legacy single-portfolio) or `{ [portfolioId]: T[] }` (multi-
 * portfolio). Reading resolves the active portfolio's slice; writing rewrites the
 * WHOLE blob (KV is last-write-wins) while preserving the stored shape and the
 * other portfolios' slices. Because the blob is rewritten whole, two concurrent
 * writers to the same key must be serialized or one silently drops the other's
 * rows; `append` additionally re-reads the fresh slice inside that queue and
 * de-dupes by `id` so a retried append can't double-write.
 *
 * Every per-list store shares this one implementation, so the array-vs-record
 * shape rule, the per-key write serialization, and the append de-dupe live in a
 * single place. Declaring a new scoped list is then one `createScopedBlobStore`
 * call — there is no per-list blob-schema or read/write wiring to copy.
 */
import { z } from 'zod';
import { resolveScopedBlob, mergeScopedBlob, type ScopedBlob } from './scopedBlob';
import { readKvBlob, writeKvBlob } from './kvBlob';

export interface ScopedBlobStore<T> {
  /** The active portfolio's slice (empty when absent). Reads are public. */
  get(portfolioId?: string): Promise<T[]>;
  /** Every slice, one list in stored order (a legacy array is the one slice). */
  all(): Promise<T[]>;
  /** Replace the portfolio's slice, preserving the stored shape + siblings. */
  save(items: T[], portfolioId?: string): Promise<void>;
  /** Append rows to the FRESH slice (de-duped by id); returns the new slice. */
  append(newRows: T[], portfolioId?: string): Promise<T[]>;
  /**
   * Replace the FRESH slice with `fn(slice)`, inside the same per-key queue —
   * the general read-modify-write that `append` is one case of (an upsert or
   * a bounded history needs the fresh slice, not the caller's copy). Returns
   * the new slice.
   */
  update(fn: (slice: T[]) => T[], portfolioId?: string): Promise<T[]>;
}

// Per-key write serialization. KV is last-write-wins, so concurrent writers to
// one key (Import + another tab + another device) are queued; each runs whether
// or not the prior write resolved so one failure can't wedge the chain.
const scopedWriteTails = new Map<string, Promise<unknown>>();
function enqueueScopedWrite<T>(key: string, op: () => Promise<T>): Promise<T> {
  const prev = scopedWriteTails.get(key) ?? Promise.resolve();
  const run = prev.then(op, op);
  scopedWriteTails.set(key, run.then(() => undefined, () => undefined));
  return run;
}

export function createScopedBlobStore<T extends { id?: string }>(
  key: string,
  itemSchema: z.ZodType<T, unknown>,
): ScopedBlobStore<T> {
  const blobSchema = z.union([
    z.array(itemSchema),
    z.record(z.string(), z.array(itemSchema)),
  ]) as z.ZodType<ScopedBlob<T>, unknown>;
  const empty: ScopedBlob<T> = [];

  return {
    async get(portfolioId) {
      return resolveScopedBlob(await readKvBlob(key, blobSchema, empty), portfolioId);
    },
    async all() {
      const blob = await readKvBlob(key, blobSchema, empty);
      return Array.isArray(blob) ? blob : Object.values(blob).flat();
    },
    async save(items, portfolioId) {
      await enqueueScopedWrite(key, async () => {
        // throwOnInvalid: a corrupt stored blob must abort the write, not read as
        // empty — mergeScopedBlob would otherwise rebuild from [] and drop every
        // other portfolio's slice on the whole-blob rewrite.
        const current = await readKvBlob(key, blobSchema, empty, { throwOnInvalid: true });
        await writeKvBlob(key, mergeScopedBlob(current, items, portfolioId));
      });
    },
    append(newRows, portfolioId) {
      return enqueueScopedWrite(key, async () => {
        const current = await readKvBlob(key, blobSchema, empty, { throwOnInvalid: true });
        const slice = resolveScopedBlob(current, portfolioId);
        const existingIds = new Set(slice.map((r) => r.id).filter(Boolean));
        const merged = [...slice, ...newRows.filter((r) => !r.id || !existingIds.has(r.id))];
        await writeKvBlob(key, mergeScopedBlob(current, merged, portfolioId));
        return merged;
      });
    },
    update(fn, portfolioId) {
      return enqueueScopedWrite(key, async () => {
        const current = await readKvBlob(key, blobSchema, empty, { throwOnInvalid: true });
        const next = fn(resolveScopedBlob(current, portfolioId));
        await writeKvBlob(key, mergeScopedBlob(current, next, portfolioId));
        return next;
      });
    },
  };
}
